import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '@/api';
import { Upload, FileText, X, Cpu, ArrowRight, DollarSign } from 'lucide-react';
import CostEstimate from '@/components/CostEstimate';
import ProcessingProgress from '@/components/ProcessingProgress';

export default function UploadPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [facilityKey, setFacilityKey] = useState('');
  const [revision, setRevision] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState('upload'); // upload | details | estimate | processing
  const [jobId, setJobId] = useState(null);
  const [costEstimate, setCostEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/tiff': ['.tiff', '.tif'],
    },
    multiple: true,
  });

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setStep('estimate');

    try {
      // Step 1: Create the job
      const jobRes = await api.post('/drawing-jobs', {
        project_name: projectName,
        facility_key: facilityKey,
        revision,
        notes,
      });
      const id = jobRes.data.id;
      setJobId(id);

      // Step 2: Upload files
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      await api.post(`/drawing-jobs/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Step 3: Get cost estimate
      setEstimating(true);
      try {
        const estRes = await api.post(`/drawing-jobs/${id}/estimate-cost`, null, { timeout: 12000 });
        setCostEstimate(estRes.data);
      } catch (err) {
        // Estimate can be slow for large sets; don't block — show a count-based estimate
        setCostEstimate({
          total_pages: files.length,
          pages_needing_vision: files.length,
          pages_skipped: 0,
          total_estimated_cost: (files.length * 0.08).toFixed(2),
          estimated_time_minutes: (files.length * 0.3).toFixed(1),
          cost_breakdown: { vision_analysis: (files.length * 0.08).toFixed(4), classification: 0 },
          per_page: [],
        });
      } finally {
        setEstimating(false);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setStep('upload');
    } finally {
      setUploading(false);
    }
  };

  const confirmProcessing = async () => {
    if (!jobId) return;
    setStep('processing');
    try {
      await api.post(`/drawing-jobs/${jobId}/process`);
    } catch (err) {
      console.error('Processing start failed:', err);
    }
  };

  const handleProcessingComplete = (status) => {
    setTimeout(() => navigate(`/jobs/${jobId}`), 1500);
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-3xl" data-testid="upload-page">
      <h1 className="font-heading font-bold text-2xl tracking-tight uppercase text-foreground mb-1">Upload Drawings</h1>
      <p className="text-sm text-muted-foreground mb-8">Upload construction drawing sets for AI-powered extraction</p>

      {step === 'processing' ? (
        <div className="space-y-4 animate-fade-in" data-testid="processing-view">
          <ProcessingProgress jobId={jobId} onComplete={handleProcessingComplete} />
          <p className="text-xs text-muted-foreground font-mono text-center">
            You can navigate away — processing continues in the background.
          </p>
        </div>
      ) : step === 'estimate' ? (
        <div className="space-y-4 animate-fade-in" data-testid="estimate-view">
          {estimating ? (
            <div className="flex flex-col items-center py-12">
              <DollarSign className="w-8 h-8 text-amber-400 animate-pulse" />
              <p className="font-heading font-bold text-sm uppercase tracking-wider text-foreground mt-4">Analyzing Drawing Set</p>
              <p className="text-xs text-muted-foreground mt-1">Classifying pages and estimating cost...</p>
            </div>
          ) : costEstimate ? (
            <CostEstimate
              estimate={costEstimate}
              onConfirm={confirmProcessing}
              confirming={false}
            />
          ) : null}
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in">
          {/* Drop Zone */}
          <div
            {...getRootProps()}
            data-testid="file-dropzone"
            className={`border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-blue-50 dark:bg-blue-950/10'
                : 'border-border hover:border-slate-600 bg-card'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className={`w-8 h-8 mx-auto mb-3 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} />
            <p className="font-heading font-bold text-sm uppercase tracking-wider text-foreground/80">
              {isDragActive ? 'Drop files here' : 'Drag & drop drawing files'}
            </p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">PDF, PNG, JPG, TIFF supported</p>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="bg-card border border-border rounded-sm divide-y divide-slate-800/50">
              {files.map((file, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5" data-testid={`uploaded-file-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="font-mono text-xs text-foreground/80 truncate">{file.name}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button onClick={() => removeFile(i)} className="p-1 hover:bg-secondary rounded text-muted-foreground hover:text-rose-400 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <div className="px-4 py-2 bg-secondary/20">
                <span className="text-[10px] font-mono text-muted-foreground">{files.length} file(s) ready</span>
              </div>
            </div>
          )}

          {/* Project Details */}
          {files.length > 0 && (
            <div className="bg-card border border-border rounded-sm p-5 space-y-4">
              <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground mb-3">Project Context</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Project Name</label>
                  <input
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    data-testid="project-name-input"
                    className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                    placeholder="e.g. Meridian Office Tower"
                  />
                </div>
                <div>
                  <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Facility Key</label>
                  <input
                    value={facilityKey}
                    onChange={(e) => setFacilityKey(e.target.value)}
                    data-testid="facility-key-input"
                    className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                    placeholder="e.g. meridian-tower"
                  />
                </div>
                <div>
                  <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Revision</label>
                  <input
                    value={revision}
                    onChange={(e) => setRevision(e.target.value)}
                    data-testid="revision-input"
                    className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                    placeholder="e.g. R1"
                  />
                </div>
                <div>
                  <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Notes</label>
                  <input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="notes-input"
                    className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                    placeholder="Optional notes"
                  />
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={uploading}
                data-testid="submit-upload-btn"
                className="flex items-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-11 px-6 transition-colors disabled:opacity-50 mt-4"
              >
                <Cpu className="w-4 h-4" />
                {uploading ? 'Processing...' : 'Start Extraction'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
